import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from './config';
import './Sidebar.css';

function Sidebar({ username, onManageAccounts }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [portfolioData, setPortfolioData] = useState({
    totalEquity: null,
    todayPnL: null,
    todayPnLPercent: null,
    equityChange: null,
    openPositionsCount: 0,
    loading: true
  });
  const [goalsData, setGoalsData] = useState({
    weeklyGoal: null,
    weeklyProgress: null,
    weeklyStatus: null,
    monthlyGoal: null,
    monthlyProgress: null,
    monthlyStatus: null,
    loading: true
  });
  const [userSettings, setUserSettings] = useState(null);

  useEffect(() => {
    fetchPortfolioSnapshot();
    fetchGoalsData();
    fetchUserSettings();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPortfolioSnapshot();
      fetchGoalsData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchUserSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio/user-settings/`);
      const settings = response.data.results || response.data;
      if (Array.isArray(settings) && settings.length > 0) {
        setUserSettings(settings[0]);
      } else if (!Array.isArray(settings) && settings) {
        setUserSettings(settings);
      }
    } catch (err) {
      console.error('Error fetching user settings:', err);
    }
  };

  const fetchPortfolioSnapshot = async () => {
    try {
      setPortfolioData(prev => ({ ...prev, loading: true }));
      
      // Fetch equity data (most recent)
      const equityResponse = await axios.get(`${API_URL}/portfolio/equity/?page_size=10`);
      const equityData = equityResponse.data.results || equityResponse.data;
      const equityArray = Array.isArray(equityData) ? equityData : [];
      
      // Sort by date (newest first) - IBKR format: DD/MM/YY
      const sortedEquity = equityArray.sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = 2000 + parseInt(parts[2], 10);
            return new Date(year, month - 1, day).getTime();
          }
          return 0;
        };
        return parseDate(b.report_date) - parseDate(a.report_date);
      });

      const latestEquity = sortedEquity[0];
      const previousEquity = sortedEquity[1];

      if (latestEquity) {
        const totalEquity = parseFloat(latestEquity.total || 0);
        const previousTotal = previousEquity ? parseFloat(previousEquity.total || 0) : totalEquity;
        const equityChange = totalEquity - previousTotal;
        const equityChangePercent = previousTotal !== 0 ? (equityChange / previousTotal) * 100 : 0;

        // Fetch positions for count
        const positionsResponse = await axios.get(`${API_URL}/portfolio/open-positions/?page_size=all`);
        const positionsData = positionsResponse.data.results || positionsResponse.data;
        const positionsArray = Array.isArray(positionsData) ? positionsData : [];
        const realPositions = positionsArray.filter(p => 
          (p.asset_category || '').toUpperCase() !== 'SYNTHETIC'
        );

        // Calculate today's P&L (unrealized from positions)
        let todayPnL = 0;
        realPositions.forEach(position => {
          const unrealizedPnL = parseFloat(position.fifo_pnl_unrealized || 0);
          todayPnL += unrealizedPnL;
        });

        const todayPnLPercent = totalEquity !== 0 ? (todayPnL / (totalEquity - todayPnL)) * 100 : 0;

        setPortfolioData({
          totalEquity,
          todayPnL,
          todayPnLPercent,
          equityChange,
          equityChangePercent,
          openPositionsCount: realPositions.length,
          loading: false
        });
      } else {
        setPortfolioData(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Error fetching portfolio snapshot:', err);
      setPortfolioData(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchGoalsData = async () => {
    try {
      setGoalsData(prev => ({ ...prev, loading: true }));
      
      // Fetch user settings for goals
      const response = await axios.get(`${API_URL}/portfolio/user-settings/`);
      const settings = response.data.results || response.data;
      const userSettings = Array.isArray(settings) && settings.length > 0 
        ? settings[0] 
        : (!Array.isArray(settings) && settings ? settings : null);

      if (!userSettings) {
        setGoalsData(prev => ({ ...prev, loading: false }));
        return;
      }

      // Fetch equity data for progress calculation
      const equityResponse = await axios.get(`${API_URL}/portfolio/equity/?page_size=100`);
      const equityData = equityResponse.data.results || equityResponse.data;
      const equityArray = Array.isArray(equityData) ? equityData : [];

      if (equityArray.length === 0) {
        setGoalsData(prev => ({ ...prev, loading: false }));
        return;
      }

      // Get most recent equity
      const sortedEquity = equityArray.sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = 2000 + parseInt(parts[2], 10);
            return new Date(year, month - 1, day).getTime();
          }
          return 0;
        };
        return parseDate(b.report_date) - parseDate(a.report_date);
      });

      const latestEquity = sortedEquity[0];
      const currentTotal = parseFloat(latestEquity.total || 0);

      // Get start equity (first of year or first available)
      const currentYear = new Date().getFullYear();
      const yearStartEquity = equityArray
        .filter(e => {
          const parts = e.report_date?.split('/');
          if (parts && parts.length === 3) {
            const year = 2000 + parseInt(parts[2], 10);
            return year === currentYear;
          }
          return false;
        })
        .sort((a, b) => {
          const parseDate = (dateStr) => {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10);
              return new Date(2000 + parseInt(parts[2], 10), month - 1, day).getTime();
            }
            return 0;
          };
          return parseDate(a.report_date) - parseDate(b.report_date);
        })[0];

      const startEquity = yearStartEquity ? parseFloat(yearStartEquity.total || 0) : currentTotal;
      const currentPnL = currentTotal - startEquity;

      // Calculate weekly progress
      const now = new Date();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - now.getDay());
      weekStart.setHours(0, 0, 0, 0);
      
      const weekEquity = equityArray
        .filter(e => {
          const parts = e.report_date?.split('/');
          if (parts && parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = 2000 + parseInt(parts[2], 10);
            const date = new Date(year, month - 1, day);
            return date >= weekStart;
          }
          return false;
        })
        .sort((a, b) => {
          const parseDate = (dateStr) => {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10);
              const year = 2000 + parseInt(parts[2], 10);
              return new Date(year, month - 1, day).getTime();
            }
            return 0;
          };
          return parseDate(a.report_date) - parseDate(b.report_date);
        })[0];

      const weekStartEquity = weekEquity ? parseFloat(weekEquity.total || 0) : currentTotal;
      const weeklyPnL = currentTotal - weekStartEquity;

      // Calculate monthly progress
      const monthNow = new Date();
      const monthStart = new Date(monthNow.getFullYear(), monthNow.getMonth(), 1);
      const monthEquity = equityArray
        .filter(e => {
          const parts = e.report_date?.split('/');
          if (parts && parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = 2000 + parseInt(parts[2], 10);
            const date = new Date(year, month - 1, day);
            return date >= monthStart;
          }
          return false;
        })
        .sort((a, b) => {
          const parseDate = (dateStr) => {
            const parts = dateStr.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10);
              const year = 2000 + parseInt(parts[2], 10);
              return new Date(year, month - 1, day).getTime();
            }
            return 0;
          };
          return parseDate(a.report_date) - parseDate(b.report_date);
        })[0];

      const monthStartEquity = monthEquity ? parseFloat(monthEquity.total || 0) : currentTotal;
      const monthlyPnL = currentTotal - monthStartEquity;

      // Weekly goal
      const weeklyGoal = userSettings.weekly_pnl_goal 
        ? parseFloat(userSettings.weekly_pnl_goal) 
        : null;
      const weeklyProgress = weeklyGoal ? (weeklyPnL / weeklyGoal) * 100 : null;
      const weeklyStatus = weeklyGoal 
        ? (weeklyPnL >= weeklyGoal ? 'on-track' : 'behind') 
        : null;

      // Monthly goal
      const monthlyGoal = userSettings.monthly_pnl_goal 
        ? parseFloat(userSettings.monthly_pnl_goal) 
        : null;
      const monthlyProgress = monthlyGoal ? (monthlyPnL / monthlyGoal) * 100 : null;
      const monthlyStatus = monthlyGoal 
        ? (monthlyPnL >= monthlyGoal ? 'on-track' : 'behind') 
        : null;

      setGoalsData({
        weeklyGoal,
        weeklyProgress,
        weeklyStatus,
        weeklyPnL,
        monthlyGoal,
        monthlyProgress,
        monthlyStatus,
        monthlyPnL,
        loading: false
      });
    } catch (err) {
      console.error('Error fetching goals data:', err);
      setGoalsData(prev => ({ ...prev, loading: false }));
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '€0.00';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.00%';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-content">
        {/* Quick Navigation */}
        <nav className="sidebar-nav">
          <h3 className="sidebar-section-title">Navigation</h3>
          <Link 
            to="/" 
            className={`sidebar-nav-link ${isActive('/') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">🏠</span>
            <span>Dashboard</span>
          </Link>
          <Link 
            to="/equity" 
            className={`sidebar-nav-link ${isActive('/equity') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">📊</span>
            <span>Equity</span>
          </Link>
          <Link 
            to="/open-positions" 
            className={`sidebar-nav-link ${isActive('/open-positions') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">📋</span>
            <span>Open Positions</span>
          </Link>
          <Link 
            to="/trades" 
            className={`sidebar-nav-link ${isActive('/trades') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">💼</span>
            <span>Trades</span>
          </Link>
          <Link 
            to="/pmcc" 
            className={`sidebar-nav-link ${isActive('/pmcc') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">🎯</span>
            <span>PMCC</span>
          </Link>
          <Link 
            to="/tools" 
            className={`sidebar-nav-link ${isActive('/tools') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">🔧</span>
            <span>Tools</span>
          </Link>
          <Link 
            to="/trading-journal" 
            className={`sidebar-nav-link ${isActive('/trading-journal') || isActive('/trade-journal') ? 'active' : ''}`}
          >
            <span className="sidebar-icon">📝</span>
            <span>Trading Journal</span>
          </Link>
        </nav>

        {/* Mini Portfolio Snapshot */}
        <div className="sidebar-section">
          <h3 className="sidebar-section-title">Portfolio</h3>
          {portfolioData.loading ? (
            <div className="sidebar-loading">Loading...</div>
          ) : (
            <>
              <div className="sidebar-stat">
                <div className="sidebar-stat-label">Total Equity</div>
                <div className="sidebar-stat-value">
                  {formatCurrency(portfolioData.totalEquity)}
                </div>
                {portfolioData.equityChange !== null && portfolioData.equityChange !== undefined && (
                  <div className={`sidebar-stat-change ${portfolioData.equityChange >= 0 ? 'positive' : 'negative'}`}>
                    {portfolioData.equityChange >= 0 ? '↑' : '↓'} {formatCurrency(Math.abs(portfolioData.equityChange))}
                    {portfolioData.equityChangePercent !== null && portfolioData.equityChangePercent !== undefined && (
                      <span> ({formatPercent(portfolioData.equityChangePercent)})</span>
                    )}
                  </div>
                )}
              </div>
              <div className="sidebar-stat">
                <div className="sidebar-stat-label">Today's P&L</div>
                <div className={`sidebar-stat-value ${portfolioData.todayPnL >= 0 ? 'positive' : 'negative'}`}>
                  {portfolioData.todayPnL >= 0 ? '+' : ''}{formatCurrency(portfolioData.todayPnL)}
                </div>
                {portfolioData.todayPnLPercent !== null && (
                  <div className={`sidebar-stat-change ${portfolioData.todayPnL >= 0 ? 'positive' : 'negative'}`}>
                    {formatPercent(portfolioData.todayPnLPercent)}
                  </div>
                )}
              </div>
              <div className="sidebar-stat">
                <div className="sidebar-stat-label">Open Positions</div>
                <div className="sidebar-stat-value">
                  {portfolioData.openPositionsCount}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Goals Progress Widget */}
        {(goalsData.weeklyGoal || goalsData.monthlyGoal) && (
          <div className="sidebar-section">
            <h3 className="sidebar-section-title">Goals</h3>
            {goalsData.loading ? (
              <div className="sidebar-loading">Loading...</div>
            ) : (
              <>
                {goalsData.weeklyGoal && (
                  <div className="sidebar-goal">
                    <div className="sidebar-goal-header">
                      <span className="sidebar-goal-label">Weekly</span>
                      <span className={`sidebar-goal-status ${goalsData.weeklyStatus}`}>
                        {goalsData.weeklyStatus === 'on-track' ? '✓' : '⚠'}
                      </span>
                    </div>
                    <div className="sidebar-goal-progress">
                      <div className="sidebar-goal-progress-bar">
                        <div 
                          className={`sidebar-goal-progress-fill ${goalsData.weeklyStatus}`}
                          style={{ width: `${Math.min(Math.max(goalsData.weeklyProgress || 0, 0), 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="sidebar-goal-amount">
                      {formatCurrency(goalsData.weeklyPnL || 0)} / {formatCurrency(goalsData.weeklyGoal)}
                    </div>
                  </div>
                )}
                {goalsData.monthlyGoal && (
                  <div className="sidebar-goal">
                    <div className="sidebar-goal-header">
                      <span className="sidebar-goal-label">Monthly</span>
                      <span className={`sidebar-goal-status ${goalsData.monthlyStatus}`}>
                        {goalsData.monthlyStatus === 'on-track' ? '✓' : '⚠'}
                      </span>
                    </div>
                    <div className="sidebar-goal-progress">
                      <div className="sidebar-goal-progress-bar">
                        <div 
                          className={`sidebar-goal-progress-fill ${goalsData.monthlyStatus}`}
                          style={{ width: `${Math.min(Math.max(goalsData.monthlyProgress || 0, 0), 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="sidebar-goal-amount">
                      {formatCurrency(goalsData.monthlyPnL || 0)} / {formatCurrency(goalsData.monthlyGoal)}
                    </div>
                  </div>
                )}
                <Link to="/equity?submenu=goals" className="sidebar-goal-link">
                  View All Goals →
                </Link>
              </>
            )}
          </div>
        )}

        {/* User Context & Settings */}
        <div className="sidebar-section sidebar-section-footer">
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{username}</div>
            <button 
              className="sidebar-settings-link"
              onClick={() => {
                navigate('/settings');
                if (onManageAccounts) {
                  onManageAccounts();
                }
              }}
            >
              <span className="sidebar-icon">⚙️</span>
              <span>Settings</span>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
